import { httpPostJson } from '@/lib/net'
import { isActive } from '@/services/feature-flag-service'
import { getUserId } from '@/services/identity-client'

export interface TelemetryRequest {
  events: TelemetryEvent[]
}

export interface TelemetryEvent {
  event_name: string
  event_params: {
    [x: string]: unknown
  }
}

/**
 * A base op any telemetry op should implement.
 */
interface Op {
  toEvent(): TelemetryEvent
}

/**
 * A pan and zoom telemetry {@link Op}.
 */
export class PanZoomOp implements Op {
  constructor(
    private lng: string,
    private lat: string,
    private zoom: string,
    private timestamp: string
  ) {}

  toEvent(): TelemetryEvent {
    return {
      event_name: 'user_pan_zoom',
      event_params: {
        centroid: `POINT(${this.lng} ${this.lat})`,
        zoom: this.zoom,
        timestamp: this.timestamp,
        user_id: '',
      },
    }
  }
}

/**
 * Queue to hold telemetry operations for processing.
 */
class TelemetryQueue {
  // Using object instead of list for queue to maintain 0(1) time complexity,
  // requardless of queue size.  With list, using the shift operator to dequeue
  // requires re-indexing entire array.  Won't matter much is queue is small,
  // but was uncertain of quantity of data queue would hold.
  private elements: Record<number, Op>
  private head: number
  private tail: number

  constructor() {
    this.elements = {}
    this.head = 0
    this.tail = 0
  }

  enqueue(element: Op) {
    this.elements[this.tail] = element
    this.tail++
  }

  dequeue() {
    const item = this.elements[this.head]
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete this.elements[this.head]
    this.head++
    return item
  }

  get length() {
    return this.tail - this.head
  }
}

const queue = new TelemetryQueue()

let isProcessing = false
let featureEnabled = false
let intervalID: number | null = null

/**
 * Starts the telemetry processor if needed.
 */
export const startTelemetry = () => {
  if (isProcessing) {
    return
  }

  isProcessing = true
  featureEnabled = isActive('content_user_pan_zoom') as boolean

  if (!featureEnabled) {
    return
  }

  startVisibleProcessor()
}

/**
 * Exposed for testing.
 */
export const setFeatureEnabled = (enabled: boolean) => {
  featureEnabled = enabled
}

/**
 * Starts events processor.  Will disable the processor when the browser
 * window isn't active.
 */
export const startVisibleProcessor = () => {
  startProcessor()

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      startProcessor()
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      window.clearInterval(intervalID!)
      intervalID = null
    }
  })
}

const startProcessor = () => {
  if (!intervalID) {
    intervalID = window.setInterval(processQueue, 30 * 1000)
  }
}

/**
 * Adds a telemetry {@link Op} to be processed.
 */
export const addTelementryOp = (op: Op) => {
  if (!featureEnabled) {
    return
  }

  queue.enqueue(op)
}

/**
 * Process queued events and posts a batch to the analytics router events
 * endpoint.
 *
 * Exported for testing.
 */
export const processQueue = async () => {
  let processed = 0
  const userId = await getUserId()
  const events: TelemetryEvent[] = []

  // Process queue until either empty or 900 items have been processed.
  // The analytics router has a limit of 1000 events set.  This limit is never
  // expected to be an issue in web, but putting a cap here to be safe.
  while (queue.length !== 0 && processed < 900) {
    processed++
    const op = queue.dequeue()
    const event = op.toEvent()

    if (op instanceof PanZoomOp) {
      event.event_params.user_id = userId
    }

    events.push(event)
  }

  // No events queued, don't send post.
  if (!events.length) {
    return
  }

  const body: TelemetryRequest = { events }

  httpPostJson('/v1/analytics/events', body).catch(() => {
    // Something went wrong.
    // Could consider requeuing events back into the processor, but opting to
    // drop for now.
  })
}
